import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import {
  ref, get, set, push, update, query,
  orderByChild, equalTo, serverTimestamp
} from 'firebase/database';
import Swal from 'sweetalert2';
import {
  PlusIcon, TrashIcon, PencilIcon, XMarkIcon,
  UserIcon, CreditCardIcon, CheckCircleIcon,
  ClockIcon, CalendarIcon, MagnifyingGlassIcon,
  BoltIcon, ArrowPathIcon, ExclamationTriangleIcon,
  CurrencyDollarIcon, PhoneIcon, IdentificationIcon,
  ChevronDownIcon, ChevronUpIcon, EyeIcon,
  QrCodeIcon, UserGroupIcon, ArchiveBoxIcon,
  SparklesIcon, SignalIcon, NoSymbolIcon,
  BanknotesIcon, DevicePhoneMobileIcon,
  ArrowRightIcon, CheckIcon,
} from '@heroicons/react/24/outline';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const SWAL_Z = { customClass: { container: 'swal-topz' } };
if (typeof document !== 'undefined' && !document.getElementById('swal-topz-style')) {
  const s = document.createElement('style');
  s.id = 'swal-topz-style';
  s.textContent = '.swal-topz{z-index:99999!important}';
  document.head.appendChild(s);
}

const toast = (icon, title, text = '') =>
  Swal.fire({
    icon, title, text, timer: 2500, toast: true,
    position: 'top-end', showConfirmButton: false, ...SWAL_Z,
  });

const PRIMARY = '#800020';
const DARK = '#1a1a2e';

// ─── TABS ─────────────────────────────────────────────────────────────────────
const TABS = [
  { key: 'active',  label: 'Active Guests',  icon: <SignalIcon    className="h-4 w-4" /> },
  { key: 'history', label: 'Visit History',  icon: <ArchiveBoxIcon className="h-4 w-4" /> },
  { key: 'cards',   label: 'RFID Cards',     icon: <QrCodeIcon    className="h-4 w-4" /> },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const formatDuration = (minutes) => {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m > 0 ? m + 'm' : ''}`.trim() : `${m}m`;
};

const formatTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });
};

const formatDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-KE', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const timeUntilExpiry = (validUntil) => {
  const diff = validUntil - Date.now();
  if (diff <= 0) return { label: 'Expired', color: 'text-red-500', urgent: true };
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(mins / 60);
  if (hrs > 0) return { label: `${hrs}h ${mins % 60}m left`, color: 'text-green-600', urgent: false };
  if (mins > 30) return { label: `${mins}m left`, color: 'text-yellow-600', urgent: false };
  return { label: `${mins}m left`, color: 'text-red-500', urgent: true };
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const GuestManagement = () => {
  const { userData, currentUser } = useAuth();

  const userRole     = (userData?.personalInfo?.role || '').toLowerCase();
  const isAdmin      = userRole === 'admin';
  const isSuperAdmin = userRole === 'superadmin';
  const isAuthorized = isAdmin || isSuperAdmin;

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeTab,      setActiveTab]      = useState('active');
  const [activeGuests,   setActiveGuests]   = useState([]);
  const [visitHistory,   setVisitHistory]   = useState([]);
  const [rfidCards,      setRfidCards]      = useState([]);
  const [activities,     setActivities]     = useState([]);
  const [users,          setUsers]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [searchQuery,    setSearchQuery]    = useState('');

  // modals
  const [activateModal,  setActivateModal]  = useState(false);
  const [completeModal,  setCompleteModal]  = useState(false);
  const [cardModal,      setCardModal]      = useState(false);
  const [selectedVisit,  setSelectedVisit]  = useState(null);

  // ── Auth guard ─────────────────────────────────────────────────────────────
  const requireAuth = () => {
    if (!isAuthorized) {
      toast('error', 'Access Denied', 'Only Admins can perform this action.');
      return false;
    }
    return true;
  };

  // ── Fetch all data ─────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [actSnap, userSnap, cardSnap, visitSnap] = await Promise.all([
        get(ref(database, 'activities')),
        get(ref(database, 'users')),
        get(ref(database, 'rfidCards')),
        get(ref(database, 'guestVisits')),
      ]);

      setActivities(actSnap.exists()
        ? Object.entries(actSnap.val())
            .map(([k, v]) => ({ id: k, ...v }))
            .filter(a => a.active !== false)
        : []);

      setUsers(userSnap.exists()
        ? Object.entries(userSnap.val()).map(([k, v]) => ({ id: k, ...v }))
        : []);

      setRfidCards(cardSnap.exists()
        ? Object.entries(cardSnap.val()).map(([k, v]) => ({ id: k, ...v }))
        : []);

      if (visitSnap.exists()) {
        const all = Object.entries(visitSnap.val()).map(([k, v]) => ({ id: k, ...v }));
        setActiveGuests(all.filter(v => v.status === 'ongoing'));
        setVisitHistory(all.filter(v => v.status === 'completed').sort((a, b) => b.checkedIn - a.checkedIn));
      } else {
        setActiveGuests([]);
        setVisitHistory([]);
      }
    } catch (err) {
      console.error(err);
      toast('error', 'Error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Auto-refresh active guests every 30s ───────────────────────────────────
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'active') fetchAll();
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab, fetchAll]);

  // ── Counts ─────────────────────────────────────────────────────────────────
  const availableCards = rfidCards.filter(c => c.status === 'available').length;
  const activeCount    = activeGuests.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── HEADER ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-3xl font-bold text-gray-900">Guest Management</h1>
              {userRole && (
                <span className="px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wide"
                  style={{
                    background: isSuperAdmin ? DARK    : isAdmin ? '#fef2f2' : '#f3f4f6',
                    color:      isSuperAdmin ? '#fff'  : isAdmin ? PRIMARY   : '#6b7280',
                    border:     `1px solid ${isSuperAdmin ? DARK : isAdmin ? '#fcd5de' : '#e5e7eb'}`,
                  }}>
                  {isSuperAdmin ? '⚡ Super Admin' : isAdmin ? '🛡 Admin' : '👁 Read Only'}
                </span>
              )}
            </div>
            <p className="text-gray-500 mt-1 text-sm">
              Manage guest visits, RFID card pool and access control
            </p>
          </div>

          {isAuthorized && (
            <button
              onClick={() => { if (!requireAuth()) return; setActivateModal(true); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transition-all text-sm"
              style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #b30000 100%)` }}>
              <PlusIcon className="h-5 w-5" />
              Activate Guest Visit
            </button>
          )}
        </div>

        {/* ── STAT CHIPS ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Active Guests',     value: activeCount,    icon: <UserGroupIcon   className="h-5 w-5" />, color: PRIMARY },
            { label: 'Cards Available',   value: availableCards, icon: <QrCodeIcon      className="h-5 w-5" />, color: '#16a34a' },
            { label: 'Total Cards',       value: rfidCards.length, icon: <CreditCardIcon className="h-5 w-5" />, color: DARK },
            { label: 'Visits Today',      value: visitHistory.filter(v => new Date(v.checkedIn).toDateString() === new Date().toDateString()).length + activeCount,
              icon: <CalendarIcon className="h-5 w-5" />, color: '#0f4c75' },
          ].map((stat, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${stat.color}15`, color: stat.color }}>
                {stat.icon}
              </div>
              <div>
                <p className="text-2xl font-black text-gray-900">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* ── TABS ── */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border border-gray-100 mb-6 w-fit">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{
                background: activeTab === tab.key ? PRIMARY : 'transparent',
                color:      activeTab === tab.key ? '#fff'  : '#6b7280',
              }}>
              {tab.icon}
              {tab.label}
              {tab.key === 'active' && activeCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs"
                  style={{
                    background: activeTab === 'active' ? 'rgba(255,255,255,0.25)' : `${PRIMARY}20`,
                    color:      activeTab === 'active' ? '#fff' : PRIMARY,
                  }}>
                  {activeCount}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── SEARCH (history tab) ── */}
        {activeTab === 'history' && (
          <div className="relative mb-5 max-w-sm">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by name, activity…"
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 bg-white"
              style={{ '--tw-ring-color': PRIMARY }}
            />
          </div>
        )}

        {/* ── CONTENT ── */}
        {loading ? (
          <div className="text-center py-24">
            <div className="inline-block w-10 h-10 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
            <p className="text-gray-500 mt-3 text-sm">Loading…</p>
          </div>
        ) : (
          <>
            {/* ACTIVE GUESTS */}
            {activeTab === 'active' && (
              <ActiveGuestsTab
                guests={activeGuests}
                isAuthorized={isAuthorized}
                onComplete={(visit) => { setSelectedVisit(visit); setCompleteModal(true); }}
                onRefresh={fetchAll}
              />
            )}

            {/* VISIT HISTORY */}
            {activeTab === 'history' && (
              <HistoryTab visits={visitHistory} searchQuery={searchQuery} />
            )}

            {/* RFID CARDS */}
            {activeTab === 'cards' && (
              <RfidCardsTab
                cards={rfidCards}
                isAuthorized={isAuthorized}
                onManage={() => { if (!requireAuth()) return; setCardModal(true); }}
                onRefresh={fetchAll}
              />
            )}
          </>
        )}
      </div>

      {/* ── MODALS ── */}
      <AnimatePresence>
        {activateModal && (
          <ActivateGuestModal
            activities={activities}
            users={users}
            rfidCards={rfidCards}
            currentUser={currentUser}
            userData={userData}
            onClose={() => setActivateModal(false)}
            onSuccess={() => { setActivateModal(false); fetchAll(); }}
          />
        )}
        {completeModal && selectedVisit && (
          <CompleteVisitModal
            visit={selectedVisit}
            currentUser={currentUser}
            userData={userData}
            onClose={() => { setCompleteModal(false); setSelectedVisit(null); }}
            onSuccess={() => { setCompleteModal(false); setSelectedVisit(null); fetchAll(); }}
          />
        )}
        {cardModal && (
          <RfidCardModal
            cards={rfidCards}
            currentUser={currentUser}
            userData={userData}
            onClose={() => setCardModal(false)}
            onSuccess={() => { setCardModal(false); fetchAll(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVE GUESTS TAB
// ═══════════════════════════════════════════════════════════════════════════════
const ActiveGuestsTab = ({ guests, isAuthorized, onComplete, onRefresh }) => {
  if (guests.length === 0) {
    return (
      <div className="text-center py-24 bg-white rounded-2xl border border-gray-200 shadow-sm">
        <UserGroupIcon className="h-14 w-14 text-gray-200 mx-auto mb-4" />
        <p className="text-gray-500 font-medium">No active guests right now</p>
        <p className="text-gray-400 text-sm mt-1">Activate a guest visit to see them here</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <AnimatePresence>
        {guests.map((visit, i) => {
          const expiry = timeUntilExpiry(visit.validUntil);
          return (
            <motion.div key={visit.id}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl shadow-md hover:shadow-lg transition-all border border-gray-100 overflow-hidden">

              {/* Top accent */}
              <div className="h-1.5" style={{ background: expiry.urgent
                ? 'linear-gradient(90deg,#ef4444,#dc2626)'
                : `linear-gradient(90deg,${PRIMARY},${PRIMARY}99)` }} />

              <div className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-gray-900">{visit.guestName || 'Unknown Guest'}</h3>
                    <p className="text-xs text-gray-400">{visit.guestPhone || 'No phone'}</p>
                  </div>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${expiry.urgent ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`}>
                    {expiry.label}
                  </span>
                </div>

                {/* Card + Activity */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm">
                    <QrCodeIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-gray-600 font-mono text-xs">{visit.cardUid || '—'}</span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500 text-xs">{visit.cardName || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <BoltIcon className="h-4 w-4 flex-shrink-0" style={{ color: PRIMARY }} />
                    <span className="font-semibold text-gray-800">{visit.activityTitle}</span>
                  </div>
                  {visit.scheduleLabels?.length > 0 && (
                    <div className="flex flex-wrap gap-1 ml-6">
                      {visit.scheduleLabels.map((s, idx) => (
                        <span key={idx} className="text-xs px-2 py-0.5 rounded-md"
                          style={{ background: `${PRIMARY}10`, color: PRIMARY }}>
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <ClockIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-xs text-gray-500">
                      In: {formatTime(visit.checkedIn)} · Expires: {formatTime(visit.validUntil)}
                    </span>
                  </div>
                </div>

                {/* Payment */}
                <div className="flex items-center justify-between mb-4 px-3 py-2 rounded-xl"
                  style={{ background: '#f9fafb' }}>
                  <span className="text-xs text-gray-500">Paid</span>
                  <div className="text-right">
                    <span className="font-bold text-gray-900 text-sm">KES {visit.amountPaid?.toLocaleString()}</span>
                    <span className="text-xs text-gray-400 ml-1.5">{visit.paymentMethod === 'cash' ? '💵 Cash' : visit.paymentMethod === 'wallet' ? '👛 Wallet' : '💳 Card'}</span>
                  </div>
                </div>

                {/* Member badge */}
                {visit.isRegisteredMember && (
                  <div className="flex items-center gap-1.5 mb-3">
                    <CheckCircleIcon className="h-4 w-4 text-blue-500" />
                    <span className="text-xs text-blue-600 font-medium">Registered Member</span>
                  </div>
                )}

                {/* Actions */}
                {isAuthorized && (
                  <button onClick={() => onComplete(visit)}
                    className="w-full py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all hover:scale-[1.02]"
                    style={{ background: `linear-gradient(135deg,${DARK},#2d2d4e)` }}>
                    <CheckCircleIcon className="h-4 w-4" />
                    Complete Visit
                  </button>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// HISTORY TAB
// ═══════════════════════════════════════════════════════════════════════════════
const HistoryTab = ({ visits, searchQuery }) => {
  const filtered = visits.filter(v => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      v.guestName?.toLowerCase().includes(q) ||
      v.activityTitle?.toLowerCase().includes(q) ||
      v.guestPhone?.toLowerCase().includes(q) ||
      v.cardUid?.toLowerCase().includes(q)
    );
  });

  if (filtered.length === 0) {
    return (
      <div className="text-center py-24 bg-white rounded-2xl border border-gray-200 shadow-sm">
        <ArchiveBoxIcon className="h-14 w-14 text-gray-200 mx-auto mb-4" />
        <p className="text-gray-500 font-medium">{searchQuery ? 'No results found' : 'No visit history yet'}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Guest', 'Activity / Schedules', 'Card', 'Check In', 'Check Out', 'Duration', 'Paid', 'Method', 'Type'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((visit, i) => (
              <tr key={visit.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-semibold text-gray-900">{visit.guestName || '—'}</p>
                  <p className="text-xs text-gray-400">{visit.guestPhone || '—'}</p>
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-800">{visit.activityTitle}</p>
                  {visit.scheduleLabels?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {visit.scheduleLabels.map((s, idx) => (
                        <span key={idx} className="text-xs px-1.5 py-0.5 rounded"
                          style={{ background: `${PRIMARY}10`, color: PRIMARY }}>{s}</span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="font-mono text-xs text-gray-600">{visit.cardUid || '—'}</span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{formatDateTime(visit.checkedIn)}</td>
                <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{formatDateTime(visit.checkedOut)}</td>
                <td className="px-4 py-3">
                  <span className="text-xs font-medium text-gray-700">{formatDuration(visit.actualDurationMinutes)}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="font-bold text-gray-900">KES {visit.amountPaid?.toLocaleString()}</span>
                  {visit.walletUsed > 0 && (
                    <p className="text-xs text-gray-400">+KES {visit.walletUsed} wallet</p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs px-2 py-1 rounded-full font-medium"
                    style={{
                      background: visit.paymentMethod === 'cash' ? '#f0fdf4' : visit.paymentMethod === 'wallet' ? '#fef2f2' : '#eff6ff',
                      color:      visit.paymentMethod === 'cash' ? '#16a34a' : visit.paymentMethod === 'wallet' ? PRIMARY       : '#1d4ed8',
                    }}>
                    {visit.paymentMethod === 'cash' ? '💵 Cash' : visit.paymentMethod === 'wallet' ? '👛 Wallet' : '💳 Card'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${visit.isRegisteredMember ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                    {visit.isRegisteredMember ? 'Member' : 'Guest'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// RFID CARDS TAB
// ═══════════════════════════════════════════════════════════════════════════════
const RfidCardsTab = ({ cards, isAuthorized, onManage }) => {
  const available = cards.filter(c => c.status === 'available');
  const active    = cards.filter(c => c.status === 'active');

  if (cards.length === 0) {
    return (
      <div className="text-center py-24 bg-white rounded-2xl border border-gray-200 shadow-sm">
        <QrCodeIcon className="h-14 w-14 text-gray-200 mx-auto mb-4" />
        <p className="text-gray-500 font-medium">No RFID cards in the pool</p>
        {isAuthorized && (
          <button onClick={onManage}
            className="mt-3 text-sm font-semibold"
            style={{ color: PRIMARY }}>
            + Add your first card
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      {isAuthorized && (
        <div className="flex justify-end mb-4">
          <button onClick={onManage}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white shadow transition-all hover:shadow-lg"
            style={{ background: `linear-gradient(135deg,${DARK},#2d2d4e)` }}>
            <PlusIcon className="h-4 w-4" />
            Manage Cards
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card, i) => (
          <motion.div key={card.id}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: card.status === 'available' ? '#f0fdf4' : `${PRIMARY}10` }}>
                  <QrCodeIcon className="h-5 w-5"
                    style={{ color: card.status === 'available' ? '#16a34a' : PRIMARY }} />
                </div>
                <div>
                  <p className="font-bold text-gray-900">{card.name}</p>
                  <p className="font-mono text-xs text-gray-400">{card.uid}</p>
                </div>
              </div>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${card.status === 'available' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                {card.status === 'available' ? 'Available' : 'In Use'}
              </span>
            </div>

            {card.status === 'active' && card.assignedToName && (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500">Assigned to</p>
                <p className="text-sm font-semibold text-gray-800">{card.assignedToName}</p>
                {card.validUntil && (
                  <p className="text-xs mt-0.5" style={{ color: timeUntilExpiry(card.validUntil).urgent ? '#ef4444' : '#16a34a' }}>
                    {timeUntilExpiry(card.validUntil).label}
                  </p>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVATE GUEST MODAL (FULL SCREEN)
// ═══════════════════════════════════════════════════════════════════════════════
const ActivateGuestModal = ({ activities, users, rfidCards, currentUser, userData, onClose, onSuccess }) => {
  // ── Guest info ─────────────────────────────────────────────────────────────
  const [guestType,      setGuestType]      = useState('unregistered'); // 'registered' | 'unregistered'
  const [selectedUserId, setSelectedUserId] = useState('');
  const [guestName,      setGuestName]      = useState('');
  const [guestPhone,     setGuestPhone]     = useState('');
  const [userSearch,     setUserSearch]     = useState('');

  // ── Activity selections: array of { activityId, scheduleIdxs[] } ──────────
  // Each entry = one activity with one or more schedule slots selected
  // selectedActivities: [{ activityId: string, scheduleIdxs: number[] }]
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [activitySearch,     setActivitySearch]     = useState('');
  const [expandedActivityId, setExpandedActivityId] = useState(''); // which activity's schedules are expanded

  // ── Card ───────────────────────────────────────────────────────────────────
  const [selectedCardId, setSelectedCardId] = useState('');

  // ── Payment ────────────────────────────────────────────────────────────────
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashAmount,    setCashAmount]    = useState('');
  const [loading,       setLoading]       = useState(false);

  // ── Derived ────────────────────────────────────────────────────────────────
  const availableCards = rfidCards.filter(c => c.status === 'available');
  const selectedCard   = rfidCards.find(c => c.id === selectedCardId);
  const selectedUser   = users.find(u => u.id === selectedUserId);
  const walletBalance  = selectedUser?.wallet?.balance || 0;

  // ── Today helpers ─────────────────────────────────────────────────────────
  const todayDate = new Date();
  const todayDay  = todayDate.toLocaleDateString('en-KE', { weekday: 'long' }).toLowerCase();
  const todayStr  = todayDate.toISOString().slice(0, 10);

  const isScheduleToday = (sch) => {
    if (sch.active === false) return false;
    if (sch.type === 'recurring')      return (sch.days || []).includes(todayDay);
    if (sch.type === 'specific_date')  return sch.date === todayStr;
    if (sch.type === 'specific_dates') return (sch.dates || []).includes(todayStr);
    return false;
  };

  const fmtScheduleTime = (sch) => {
    const t = sch.time || '';
    if (!t) return '';
    const [h, m] = t.split(':');
    const hour = parseInt(h);
    return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  };

  const scheduleChipLabel = (sch) => {
    if (sch.type === 'recurring') {
      const days = (sch.days || []).map(d => d.slice(0,1).toUpperCase() + d.slice(1,3)).join(', ');
      return `${days} · ${fmtScheduleTime(sch)}`;
    }
    if (sch.type === 'specific_date')  return `${sch.date} · ${fmtScheduleTime(sch)}`;
    if (sch.type === 'specific_dates') return `${(sch.dates||[]).length} dates · ${fmtScheduleTime(sch)}`;
    return fmtScheduleTime(sch);
  };

  // Activities with today's schedules only
  const todayActivities = activities
    .map(act => ({
      ...act,
      todaySchedules: (act.schedules || [])
        .map((sch, idx) => ({ ...sch, originalIdx: idx }))
        .filter(isScheduleToday),
    }))
    .filter(act => act.todaySchedules.length > 0);

  const filteredActivities = todayActivities.filter(act => {
    if (!activitySearch) return true;
    const q = activitySearch.toLowerCase();
    return (
      act.title?.toLowerCase().includes(q) ||
      act.name?.toLowerCase().includes(q)  ||
      (act.trainers || []).some(t => t.toLowerCase().includes(q))
    );
  });

  // Already-selected activity IDs
  const selectedActivityIds = selectedActivities.map(s => s.activityId);

  // Total price = sum of all selected activities' prices
  const totalPrice = selectedActivities.reduce((sum, sel) => {
    const act = activities.find(a => a.id === sel.activityId);
    return sum + (act?.pricePerVisit || 0);
  }, 0);

  // Total schedules selected across all activities
  const totalSchedulesSelected = selectedActivities.reduce((sum, s) => sum + s.scheduleIdxs.length, 0);

  // Total duration = 120min per schedule slot across all activities, min 120
  const totalDurationMinutes = totalSchedulesSelected > 0 ? totalSchedulesSelected * 120 : 120;

  // ── Activity selection handlers ───────────────────────────────────────────
  const toggleActivitySelection = (actId) => {
    setSelectedActivities(prev => {
      const exists = prev.find(s => s.activityId === actId);
      if (exists) {
        // deselect — remove from list
        return prev.filter(s => s.activityId !== actId);
      } else {
        // select — add with empty schedules, expand it
        setExpandedActivityId(actId);
        return [...prev, { activityId: actId, scheduleIdxs: [] }];
      }
    });
  };

  const toggleScheduleForActivity = (actId, schIdx) => {
    setSelectedActivities(prev =>
      prev.map(s => {
        if (s.activityId !== actId) return s;
        const already = s.scheduleIdxs.includes(schIdx);
        return {
          ...s,
          scheduleIdxs: already
            ? s.scheduleIdxs.filter(i => i !== schIdx)
            : [...s.scheduleIdxs, schIdx],
        };
      })
    );
  };

  const getScheduleIdxsForActivity = (actId) =>
    selectedActivities.find(s => s.activityId === actId)?.scheduleIdxs || [];

  // ── User search ───────────────────────────────────────────────────────────
  const filteredUsers = users.filter(u => {
    const q = userSearch.toLowerCase();
    return (
      u.personalInfo?.name?.toLowerCase().includes(q)  ||
      u.personalInfo?.phone?.toLowerCase().includes(q) ||
      u.personalInfo?.email?.toLowerCase().includes(q)
    );
  }).slice(0, 8);

  // ── Payment derived ───────────────────────────────────────────────────────
  const cashPaid   = parseFloat(cashAmount) || 0;
  const cashExcess = paymentMethod === 'cash' ? Math.max(0, cashPaid - totalPrice) : 0;
  const cashShort  = paymentMethod === 'cash' ? Math.max(0, totalPrice - cashPaid) : 0;
  const walletCovers    = paymentMethod === 'wallet' && walletBalance >= totalPrice;
  const walletRemainder = paymentMethod === 'wallet' ? Math.max(0, totalPrice - walletBalance) : 0;

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (guestType === 'registered' && !selectedUserId)
      return toast('error', 'Select Member', 'Please select a registered member');
    if (selectedActivities.length === 0)
      return toast('error', 'Select Activity', 'Please select at least one activity');
    if (!selectedCardId)
      return toast('error', 'Select Card', 'Please select an RFID card');

    if (paymentMethod === 'cash') {
      if (!cashAmount || cashPaid <= 0)
        return toast('error', 'Enter Amount', 'Please enter the cash amount received');
      if (cashShort > 0)
        return toast('error', 'Insufficient Cash', `Cash is KES ${cashShort.toLocaleString()} short`);
    }
    if (paymentMethod === 'wallet') {
      if (!selectedUserId)
        return toast('error', 'No Member Selected', 'Wallet payment requires a registered member');
      if (walletBalance < totalPrice)
        return toast('error', 'Insufficient Wallet', `Wallet KES ${walletBalance.toLocaleString()} < KES ${totalPrice.toLocaleString()}`);
    }
    if (paymentMethod === 'online') {
      Swal.fire({
        title: 'Online Payment Coming Soon',
        html: `<p style="color:#6b7280;font-size:14px">Online payments via Paystack are being set up.<br/>Please use Cash or Wallet for now.</p>`,
        icon: 'info', confirmButtonColor: PRIMARY, confirmButtonText: 'OK', ...SWAL_Z,
      });
      return;
    }

    setLoading(true);
    try {
      const now        = Date.now();
      const validUntil = now + totalDurationMinutes * 60 * 1000;

      // Guest identity
      let guestId, guestDisplayName, guestDisplayPhone, isRegisteredMember;
      if (guestType === 'registered' && selectedUserId) {
        guestId            = selectedUserId;
        guestDisplayName   = selectedUser?.personalInfo?.name  || 'Unknown';
        guestDisplayPhone  = selectedUser?.personalInfo?.phone || '';
        isRegisteredMember = true;
      } else {
        const counterSnap = await get(ref(database, 'system/counters/guestNumber'));
        const guestNumber = (counterSnap.val() || 0) + 1;
        await set(ref(database, 'system/counters/guestNumber'), guestNumber);
        guestId            = `guest_${guestNumber}`;
        guestDisplayName   = guestName.trim() || `Guest ${guestNumber}`;
        guestDisplayPhone  = guestPhone.trim();
        isRegisteredMember = false;
      }

      // Build activities snapshot for the record
      const activitiesSnapshot = selectedActivities.map(sel => {
        const act = activities.find(a => a.id === sel.activityId);
        const scheduleLabels = sel.scheduleIdxs.map(idx => {
          const sch = (act?.schedules || [])[idx];
          return sch?.label || sch?.type || `Schedule ${idx + 1}`;
        });
        return {
          activityId:     sel.activityId,
          activityTitle:  act?.title  || '',
          activityName:   act?.name   || '',
          price:          act?.pricePerVisit || 0,
          scheduleIdxs:   sel.scheduleIdxs,
          scheduleLabels,
        };
      });

      // For backward compat / card label — use first activity title
      const primaryActivityTitle = activitiesSnapshot[0]?.activityTitle || '';

      // All schedule labels flat
      const allScheduleLabels = activitiesSnapshot.flatMap(a => a.scheduleLabels);

      // Payment
      let amountPaid       = totalPrice;
      let walletUsed       = 0;
      let walletCredited   = 0;
      let paymentReference = `CASH_${now}`;
      let walletEntryId    = null;

      if (paymentMethod === 'cash') {
        if (cashExcess > 0 && guestType === 'registered' && selectedUserId) {
          const walletEntryRef = push(ref(database, `users/${selectedUserId}/wallet/entries`));
          walletEntryId = walletEntryRef.key;
          const newBalance = walletBalance + cashExcess;
          const txnRef = push(ref(database, `users/${selectedUserId}/wallet/transactions`));
          await Promise.all([
            set(walletEntryRef, {
              amount: cashExcess, remaining: cashExcess,
              source: 'guest_visit_cash_excess',
              reason: `Cash overpayment — Guest visit: ${activitiesSnapshot.map(a => a.activityTitle).join(', ')}`,
              dateAdded: now,
            }),
            set(txnRef, {
              date: now, type: 'credit', amount: cashExcess,
              source: 'cash_excess',
              description: `Cash overpayment — ${primaryActivityTitle}`,
              balance: newBalance,
            }),
            update(ref(database, `users/${selectedUserId}/wallet`), { balance: newBalance }),
          ]);
          walletCredited = cashExcess;
        }
      }

      if (paymentMethod === 'wallet') {
        walletUsed = totalPrice;
        const entriesSnap = await get(ref(database, `users/${selectedUserId}/wallet/entries`));
        let remaining = totalPrice;
        const entryUpdates = {};
        if (entriesSnap.exists()) {
          const entries = Object.entries(entriesSnap.val())
            .map(([k, v]) => ({ key: k, ...v }))
            .filter(e => e.remaining > 0)
            .sort((a, b) => a.dateAdded - b.dateAdded);
          for (const entry of entries) {
            if (remaining <= 0) break;
            const deduct = Math.min(entry.remaining, remaining);
            entryUpdates[`users/${selectedUserId}/wallet/entries/${entry.key}/remaining`] = entry.remaining - deduct;
            remaining -= deduct;
          }
        }
        const newWalletBalance = walletBalance - totalPrice;
        const txnRef = push(ref(database, `users/${selectedUserId}/wallet/transactions`));
        await Promise.all([
          ...Object.entries(entryUpdates).map(([path, val]) => set(ref(database, path), val)),
          set(txnRef, {
            date: now, type: 'debit', amount: totalPrice,
            source: 'guest_visit',
            description: `Guest visit — ${activitiesSnapshot.map(a => a.activityTitle).join(', ')}`,
            balance: newWalletBalance,
          }),
          update(ref(database, `users/${selectedUserId}/wallet`), { balance: newWalletBalance }),
        ]);
        paymentReference = `WALLET_${now}`;
      }

      // Visit record
      const visitData = {
        guestId, guestName: guestDisplayName, guestPhone: guestDisplayPhone,
        isRegisteredMember,
        userId: guestType === 'registered' ? selectedUserId : null,

        // Multi-activity
        activities:          activitiesSnapshot,
        activityCount:       activitiesSnapshot.length,
        // Primary activity fields (for display in cards/table)
        activityId:          activitiesSnapshot[0]?.activityId    || '',
        activityTitle:       primaryActivityTitle,
        scheduleLabels:      allScheduleLabels,

        // Card
        cardId: selectedCardId, cardUid: selectedCard?.uid || '', cardName: selectedCard?.name || '',

        // Payment
        totalPrice, amountPaid,
        cashReceived:  paymentMethod === 'cash' ? cashPaid : 0,
        cashChange:    paymentMethod === 'cash' ? cashExcess : 0,
        walletUsed, walletCredited, walletEntryId,
        paymentMethod, paymentReference, paymentStatus: 'paid',

        // Timing
        checkedIn: now, validUntil, checkedOut: null,
        actualDurationMinutes: null,
        plannedDurationMinutes: totalDurationMinutes,

        // Meta
        status: 'ongoing',
        activatedBy: currentUser?.uid || '',
        activatedByName: userData?.personalInfo?.name || 'Admin',
        createdAt: now,
      };

      // Also keep amountPaid alias for display
      visitData.amountPaid = totalPrice;

      const visitRef = push(ref(database, 'guestVisits'));
      const visitId  = visitRef.key;
      await set(visitRef, { ...visitData, id: visitId });

      if (guestType === 'registered' && selectedUserId) {
        await set(ref(database, `users/${selectedUserId}/guestVisits/${visitId}`), { ...visitData, id: visitId });
      }

      await update(ref(database, `rfidCards/${selectedCardId}`), {
        status: 'active', assignedTo: guestId, assignedToName: guestDisplayName,
        visitId, validUntil,
        activity: activitiesSnapshot.map(a => a.activityTitle).join(', '),
        activatedBy: currentUser?.uid || '', activatedAt: now,
      });

      const logRef = push(ref(database, 'accessLogs'));
      await set(logRef, {
        userId: guestId, userName: guestDisplayName,
        method: 'rfid_activation', status: 'activated',
        reason: `Guest visit activated — ${activitiesSnapshot.map(a => a.activityTitle).join(', ')}`,
        timestamp: now, rfidCard: selectedCard?.uid || '',
        activatedBy: currentUser?.uid || '',
      });

      toast('success', 'Guest Visit Activated!', `${guestDisplayName} is now checked in`);
      onSuccess();
    } catch (err) {
      console.error(err);
      toast('error', 'Error', 'Failed to activate guest visit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', maxWidth: '100vw', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: `linear-gradient(135deg,${DARK} 0%,${PRIMARY} 100%)` }}>
          <div>
            <h2 className="text-xl font-bold text-white">Activate Guest Visit</h2>
            <p className="text-white/60 text-sm mt-0.5">Assign activity, RFID card and process payment</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white transition-all"
            style={{ background: 'rgba(255,255,255,0.15)' }}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
          <div className="max-w-5xl mx-auto px-8 py-8 space-y-8">

            {/* ── STEP 1: GUEST TYPE ── */}
            <ModalSection title="Step 1 — Guest Identity">
              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { key: 'unregistered', label: 'Walk-in Guest',        desc: 'Not in the system',      icon: <UserIcon       className="h-5 w-5" /> },
                  { key: 'registered',   label: 'Registered Member',    desc: 'Has an account',         icon: <CheckCircleIcon className="h-5 w-5" /> },
                ].map(opt => (
                  <button key={opt.key} type="button"
                    onClick={() => { setGuestType(opt.key); setSelectedUserId(''); }}
                    className="p-4 rounded-xl border-2 text-left transition-all"
                    style={{
                      borderColor: guestType === opt.key ? PRIMARY : '#e5e7eb',
                      background:  guestType === opt.key ? `${PRIMARY}08` : '#fff',
                    }}>
                    <div className="mb-2" style={{ color: PRIMARY }}>{opt.icon}</div>
                    <p className="font-bold text-gray-900 text-sm">{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                  </button>
                ))}
              </div>

              {guestType === 'registered' ? (
                <div>
                  <ModalLabel>Search Member</ModalLabel>
                  <div className="relative mb-2">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      placeholder="Search by name, phone or email…"
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                      style={{ '--tw-ring-color': PRIMARY }}
                    />
                  </div>
                  {userSearch && (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      {filteredUsers.length === 0
                        ? <p className="px-4 py-3 text-sm text-gray-400">No members found</p>
                        : filteredUsers.map(user => (
                          <button key={user.id} type="button"
                            onClick={() => { setSelectedUserId(user.id); setUserSearch(user.personalInfo?.name || ''); }}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 text-left">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                              style={{ background: PRIMARY }}>
                              {(user.personalInfo?.name || 'U')[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 text-sm">{user.personalInfo?.name}</p>
                              <p className="text-xs text-gray-400">{user.personalInfo?.phone} · {user.personalInfo?.email}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${user.membership?.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                {user.membership?.status || 'No membership'}
                              </span>
                              {user.wallet?.balance > 0 && (
                                <p className="text-xs text-gray-400 mt-0.5">Wallet: KES {user.wallet.balance.toLocaleString()}</p>
                              )}
                            </div>
                          </button>
                        ))
                      }
                    </div>
                  )}
                  {selectedUserId && selectedUser && (
                    <div className="mt-3 flex items-center gap-3 p-3 rounded-xl border"
                      style={{ borderColor: `${PRIMARY}30`, background: `${PRIMARY}05` }}>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                        style={{ background: PRIMARY }}>
                        {(selectedUser.personalInfo?.name || 'U')[0].toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-gray-900">{selectedUser.personalInfo?.name}</p>
                        <p className="text-xs text-gray-500">{selectedUser.personalInfo?.phone}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Wallet Balance</p>
                        <p className="font-bold" style={{ color: PRIMARY }}>KES {(selectedUser.wallet?.balance || 0).toLocaleString()}</p>
                      </div>
                      <button type="button" onClick={() => { setSelectedUserId(''); setUserSearch(''); }}
                        className="text-gray-400 hover:text-red-500 ml-2">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <ModalLabel>Guest Name <span className="font-normal text-gray-400 normal-case">(optional)</span></ModalLabel>
                    <div className="relative">
                      <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input value={guestName} onChange={e => setGuestName(e.target.value)}
                        placeholder="e.g., James Omondi"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                        style={{ '--tw-ring-color': PRIMARY }} />
                    </div>
                  </div>
                  <div>
                    <ModalLabel>Phone Number <span className="font-normal text-gray-400 normal-case">(optional)</span></ModalLabel>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input value={guestPhone} onChange={e => setGuestPhone(e.target.value)}
                        placeholder="+254 712 345 678"
                        className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                        style={{ '--tw-ring-color': PRIMARY }} />
                    </div>
                  </div>
                </div>
              )}
            </ModalSection>

            {/* ── STEP 2: ACTIVITY & SCHEDULES ── */}
            <ModalSection title="Step 2 — Activities & Schedules">

              {/* Today banner */}
              <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-xl text-xs font-semibold"
                style={{ background: `${PRIMARY}08`, color: PRIMARY, border: `1px solid ${PRIMARY}20` }}>
                <CalendarIcon className="h-4 w-4 flex-shrink-0" />
                Showing activities with schedules today —{' '}
                {todayDate.toLocaleDateString('en-KE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </div>

              {/* Selected summary chips */}
              {selectedActivities.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedActivities.map(sel => {
                    const act = activities.find(a => a.id === sel.activityId);
                    return (
                      <div key={sel.activityId}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-semibold border"
                        style={{ background: `${PRIMARY}08`, borderColor: `${PRIMARY}30`, color: PRIMARY }}>
                        <BoltIcon className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>{act?.title}</span>
                        {sel.scheduleIdxs.length > 0 && (
                          <span className="px-1.5 py-0.5 rounded-full text-white text-xs"
                            style={{ background: PRIMARY }}>
                            {sel.scheduleIdxs.length} sch
                          </span>
                        )}
                        <button type="button"
                          onClick={() => toggleActivitySelection(sel.activityId)}
                          className="hover:text-red-600 transition-colors ml-0.5">
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    );
                  })}
                  <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-xs font-bold"
                    style={{ background: '#f0fdf4', color: '#16a34a' }}>
                    Total: KES {totalPrice.toLocaleString()}
                  </div>
                </div>
              )}

              {/* Search */}
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={activitySearch}
                  onChange={e => setActivitySearch(e.target.value)}
                  placeholder="Search activities by name or trainer…"
                  className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all bg-white"
                  style={{ '--tw-ring-color': PRIMARY }}
                />
                {activitySearch && (
                  <button type="button" onClick={() => setActivitySearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              <ModalLabel>
                Select Activities *
                <span className="font-normal text-gray-400 normal-case ml-1">
                  (select one or more — click to expand schedules)
                </span>
              </ModalLabel>

              {activities.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No activities found. Please create activities first.</p>
              ) : filteredActivities.length === 0 ? (
                <div className="flex flex-col items-center py-10 text-center">
                  <CalendarIcon className="h-10 w-10 text-gray-200 mb-2" />
                  <p className="text-sm font-medium text-gray-500">No activities scheduled for today</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {activitySearch ? 'Try a different search term' : 'Check activity schedules in Activity Management'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredActivities.map(act => {
                    const isSelected    = selectedActivityIds.includes(act.id);
                    const isExpanded    = expandedActivityId === act.id;
                    const schIdxs       = getScheduleIdxsForActivity(act.id);

                    return (
                      <motion.div key={act.id}
                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        className="rounded-2xl border-2 overflow-hidden transition-all"
                        style={{
                          borderColor: isSelected ? PRIMARY : '#e5e7eb',
                          background:  isSelected ? `${PRIMARY}04` : '#fff',
                        }}>

                        {/* Activity row — click to toggle select + expand */}
                        <div
                          className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => {
                            toggleActivitySelection(act.id);
                            setExpandedActivityId(isExpanded ? '' : act.id);
                          }}>

                          {/* Checkbox */}
                          <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all"
                            style={{
                              borderColor: isSelected ? PRIMARY : '#d1d5db',
                              background:  isSelected ? PRIMARY  : 'transparent',
                            }}>
                            {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                          </div>

                          {/* Thumbnail */}
                          {act.photos?.[0] ? (
                            <img src={act.photos[0]} alt={act.title}
                              className="w-14 h-14 object-cover rounded-xl flex-shrink-0" />
                          ) : (
                            <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                              style={{ background: `${PRIMARY}10` }}>
                              <BoltIcon className="h-6 w-6" style={{ color: PRIMARY }} />
                            </div>
                          )}

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-900">{act.title}</p>
                            {act.trainers?.length > 0 && (
                              <p className="text-xs text-gray-400 mt-0.5">{act.trainers.join(' · ')}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                                style={{ background: '#f0fdf4', color: '#16a34a' }}>
                                {act.todaySchedules.length} schedule{act.todaySchedules.length > 1 ? 's' : ''} today
                              </span>
                              {act.pricePerVisit > 0 && (
                                <span className="text-xs font-bold" style={{ color: PRIMARY }}>
                                  KES {Number(act.pricePerVisit).toLocaleString()} / visit
                                </span>
                              )}
                              {isSelected && schIdxs.length > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full font-semibold text-white"
                                  style={{ background: PRIMARY }}>
                                  {schIdxs.length} selected
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Expand arrow */}
                          <div className="flex-shrink-0 transition-transform duration-200"
                            style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                          </div>
                        </div>

                        {/* Schedules panel — always visible when expanded, regardless of selection */}
                        <AnimatePresence>
                          {isExpanded && act.todaySchedules.length > 0 && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.22 }}
                              className="overflow-hidden border-t"
                              style={{ borderColor: `${PRIMARY}15` }}>
                              <div className="px-4 pt-3 pb-4 bg-gray-50/50">
                                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2.5">
                                  {isSelected
                                    ? 'Select schedule(s) attending — multiple allowed'
                                    : 'Schedules today (select activity first)'}
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {act.todaySchedules.map(sch => {
                                    const isSchSelected = isSelected && schIdxs.includes(sch.originalIdx);
                                    return (
                                      <button key={sch.originalIdx} type="button"
                                        disabled={!isSelected}
                                        onClick={e => {
                                          e.stopPropagation();
                                          if (isSelected) toggleScheduleForActivity(act.id, sch.originalIdx);
                                        }}
                                        className="flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all"
                                        style={{
                                          borderColor: isSchSelected ? PRIMARY : '#e5e7eb',
                                          background:  isSchSelected ? `${PRIMARY}10` : '#fff',
                                          opacity:     !isSelected ? 0.45 : 1,
                                          cursor:      !isSelected ? 'not-allowed' : 'pointer',
                                        }}>
                                        {/* Checkbox */}
                                        <div className="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0"
                                          style={{
                                            borderColor: isSchSelected ? PRIMARY : '#d1d5db',
                                            background:  isSchSelected ? PRIMARY  : 'transparent',
                                          }}>
                                          {isSchSelected && <CheckIcon className="h-3 w-3 text-white" />}
                                        </div>
                                        {/* Icon */}
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                          style={{ background: isSchSelected ? `${PRIMARY}20` : '#f3f4f6' }}>
                                          {sch.type === 'recurring'
                                            ? <BoltIcon     className="h-4 w-4" style={{ color: isSchSelected ? PRIMARY : '#9ca3af' }} />
                                            : <CalendarIcon className="h-4 w-4" style={{ color: isSchSelected ? PRIMARY : '#9ca3af' }} />
                                          }
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          {sch.label && (
                                            <p className="text-xs font-bold text-gray-800 truncate">{sch.label}</p>
                                          )}
                                          <p className="text-xs text-gray-500 truncate">{scheduleChipLabel(sch)}</p>
                                        </div>
                                        {isSchSelected && (
                                          <CheckCircleIcon className="h-4 w-4 flex-shrink-0" style={{ color: PRIMARY }} />
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>

                                {/* Per-activity schedule count */}
                                {isSelected && schIdxs.length > 0 && (
                                  <div className="mt-2.5 flex items-center justify-between px-3 py-2 rounded-xl"
                                    style={{ background: `${PRIMARY}08`, border: `1px solid ${PRIMARY}15` }}>
                                    <p className="text-xs font-semibold" style={{ color: PRIMARY }}>
                                      {schIdxs.length} schedule{schIdxs.length > 1 ? 's' : ''} selected for {act.title}
                                    </p>
                                    <p className="text-xs text-gray-500">~{formatDuration(schIdxs.length * 120)}</p>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              )}

              {/* Grand total summary */}
              {selectedActivities.length > 1 && (
                <div className="mt-4 p-4 rounded-xl border"
                  style={{ background: `${PRIMARY}05`, borderColor: `${PRIMARY}20` }}>
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">Visit Summary</p>
                  <div className="space-y-1.5">
                    {selectedActivities.map(sel => {
                      const act = activities.find(a => a.id === sel.activityId);
                      return (
                        <div key={sel.activityId} className="flex justify-between text-sm">
                          <span className="text-gray-700">{act?.title}
                            {sel.scheduleIdxs.length > 0 && (
                              <span className="text-gray-400 text-xs ml-1">({sel.scheduleIdxs.length} sch)</span>
                            )}
                          </span>
                          <span className="font-semibold">KES {(act?.pricePerVisit || 0).toLocaleString()}</span>
                        </div>
                      );
                    })}
                    <div className="flex justify-between font-black text-base pt-1 border-t"
                      style={{ borderColor: `${PRIMARY}20`, color: PRIMARY }}>
                      <span>Total</span>
                      <span>KES {totalPrice.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
            </ModalSection>

            {/* ── STEP 3: RFID CARD ── */}
            <ModalSection title="Step 3 — Assign RFID Card">
              {availableCards.length === 0 ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700">No RFID cards available. Please return a card or add more to the pool.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {availableCards.map(card => (
                    <button key={card.id} type="button"
                      onClick={() => setSelectedCardId(card.id)}
                      className="flex flex-col items-center p-4 rounded-xl border-2 transition-all hover:shadow-sm"
                      style={{
                        borderColor: selectedCardId === card.id ? PRIMARY : '#e5e7eb',
                        background:  selectedCardId === card.id ? `${PRIMARY}08` : '#fff',
                      }}>
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-2"
                        style={{ background: selectedCardId === card.id ? `${PRIMARY}15` : '#f9fafb' }}>
                        <QrCodeIcon className="h-5 w-5" style={{ color: selectedCardId === card.id ? PRIMARY : '#9ca3af' }} />
                      </div>
                      <p className="font-bold text-gray-900 text-sm text-center">{card.name}</p>
                      <p className="font-mono text-xs text-gray-400 mt-0.5 text-center">{card.uid}</p>
                    </button>
                  ))}
                </div>
              )}
            </ModalSection>

            {/* ── STEP 4: PAYMENT ── */}
            <ModalSection title="Step 4 — Payment">
              {/* Price display */}
              {selectedActivities.length > 0 && (
                <div className="flex items-center justify-between p-4 rounded-xl mb-5"
                  style={{ background: `${PRIMARY}05`, border: `1px solid ${PRIMARY}20` }}>
                  <div>
                    <p className="text-xs text-gray-500">Total Amount Due</p>
                    <p className="font-black text-2xl" style={{ color: PRIMARY }}>
                      KES {totalPrice.toLocaleString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">{selectedActivities.length} activit{selectedActivities.length > 1 ? 'ies' : 'y'}</p>
                    <p className="text-xs text-gray-400">{totalSchedulesSelected} schedule{totalSchedulesSelected !== 1 ? 's' : ''}</p>
                  </div>
                </div>
              )}

              {/* Payment method */}
              <ModalLabel>Payment Method *</ModalLabel>
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { key: 'cash',   label: 'Cash',   icon: <BanknotesIcon        className="h-5 w-5" />, desc: 'Collect cash now' },
                  { key: 'wallet', label: 'Wallet', icon: <CurrencyDollarIcon   className="h-5 w-5" />, desc: guestType === 'registered' && selectedUserId ? `KES ${walletBalance.toLocaleString()} available` : 'Members only' },
                  { key: 'online', label: 'Online', icon: <DevicePhoneMobileIcon className="h-5 w-5" />, desc: 'Paystack (coming soon)' },
                ].map(opt => {
                  const disabled = opt.key === 'wallet' && (guestType !== 'registered' || !selectedUserId);
                  return (
                    <button key={opt.key} type="button"
                      disabled={disabled}
                      onClick={() => !disabled && setPaymentMethod(opt.key)}
                      className="p-4 rounded-xl border-2 text-left transition-all"
                      style={{
                        borderColor: paymentMethod === opt.key ? PRIMARY : '#e5e7eb',
                        background:  paymentMethod === opt.key ? `${PRIMARY}08` : disabled ? '#f9fafb' : '#fff',
                        opacity:     disabled ? 0.5 : 1,
                        cursor:      disabled ? 'not-allowed' : 'pointer',
                      }}>
                      <div className="mb-2" style={{ color: paymentMethod === opt.key ? PRIMARY : '#9ca3af' }}>{opt.icon}</div>
                      <p className="font-bold text-gray-900 text-sm">{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </button>
                  );
                })}
              </div>

              {/* Cash input */}
              {paymentMethod === 'cash' && (
                <div className="space-y-3">
                  <div>
                    <ModalLabel>Cash Received (KES) *</ModalLabel>
                    <div className="relative max-w-xs">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400">KES</span>
                      <input
                        type="number" min="0" value={cashAmount}
                        onChange={e => setCashAmount(e.target.value)}
                        placeholder={totalPrice.toString()}
                        className="w-full pl-12 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                        style={{ '--tw-ring-color': PRIMARY }}
                      />
                    </div>
                  </div>

                  {cashAmount && totalPrice > 0 && (
                    <div className="p-4 rounded-xl space-y-2"
                      style={{ background: cashShort > 0 ? '#fef2f2' : '#f0fdf4', border: `1px solid ${cashShort > 0 ? '#fcd5de' : '#bbf7d0'}` }}>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Activity price</span>
                        <span className="font-semibold">KES {totalPrice.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Cash received</span>
                        <span className="font-semibold">KES {cashPaid.toLocaleString()}</span>
                      </div>
                      {cashShort > 0 && (
                        <div className="flex justify-between text-sm border-t pt-2" style={{ borderColor: '#fcd5de' }}>
                          <span className="font-bold text-red-600">Short by</span>
                          <span className="font-bold text-red-600">KES {cashShort.toLocaleString()}</span>
                        </div>
                      )}
                      {cashExcess > 0 && (
                        <>
                          <div className="flex justify-between text-sm border-t pt-2" style={{ borderColor: '#bbf7d0' }}>
                            <span className="font-bold text-green-700">Change / Excess</span>
                            <span className="font-bold text-green-700">KES {cashExcess.toLocaleString()}</span>
                          </div>
                          {guestType === 'registered' && selectedUserId && (
                            <p className="text-xs text-green-600">✓ Excess KES {cashExcess.toLocaleString()} will be credited to member's wallet</p>
                          )}
                          {guestType !== 'registered' && (
                            <p className="text-xs text-gray-500">ℹ Return KES {cashExcess.toLocaleString()} change to guest</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Wallet info */}
              {paymentMethod === 'wallet' && selectedUserId && (
                <div className="p-4 rounded-xl space-y-2"
                  style={{ background: walletCovers ? '#f0fdf4' : '#fef2f2', border: `1px solid ${walletCovers ? '#bbf7d0' : '#fcd5de'}` }}>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Wallet balance</span>
                    <span className="font-semibold">KES {walletBalance.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Total due</span>
                    <span className="font-semibold">KES {totalPrice.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm border-t pt-2" style={{ borderColor: walletCovers ? '#bbf7d0' : '#fcd5de' }}>
                    <span className={`font-bold ${walletCovers ? 'text-green-700' : 'text-red-600'}`}>
                      {walletCovers ? 'Remaining after' : 'Short by'}
                    </span>
                    <span className={`font-bold ${walletCovers ? 'text-green-700' : 'text-red-600'}`}>
                      KES {walletCovers ? (walletBalance - totalPrice).toLocaleString() : walletRemainder.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </ModalSection>

          </div>
        </form>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4 border-t border-gray-100 bg-white">
          <button type="button" onClick={onClose}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading || availableCards.length === 0}
            className="px-10 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg transition-all disabled:opacity-50 flex items-center gap-2"
            style={{ background: `linear-gradient(135deg,${PRIMARY},#b30000)` }}>
            {loading
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Activating…</>
              : <><BoltIcon className="h-4 w-4" /> Activate Visit</>
            }
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// COMPLETE VISIT MODAL (FULL SCREEN)
// ═══════════════════════════════════════════════════════════════════════════════
const CompleteVisitModal = ({ visit, currentUser, userData, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const now       = Date.now();
  const duration  = Math.floor((now - visit.checkedIn) / 60000);

  const handleComplete = async () => {
    setLoading(true);
    try {
      const checkOutTime = Date.now();
      const actualDuration = Math.floor((checkOutTime - visit.checkedIn) / 60000);

      const completionData = {
        status:               'completed',
        checkedOut:           checkOutTime,
        actualDurationMinutes: actualDuration,
        completedBy:          currentUser?.uid || '',
        completedByName:      userData?.personalInfo?.name || 'Admin',
      };

      // Update main guestVisits record
      await update(ref(database, `guestVisits/${visit.id}`), completionData);

      // If registered member → update their user record too
      if (visit.isRegisteredMember && visit.userId) {
        await update(ref(database, `users/${visit.userId}/guestVisits/${visit.id}`), completionData);
      }

      // Free up the RFID card
      await update(ref(database, `rfidCards/${visit.cardId}`), {
        status:         'available',
        assignedTo:     '',
        assignedToName: '',
        visitId:        '',
        validUntil:     0,
        activity:       '',
        activatedBy:    '',
        activatedAt:    0,
      });

      // Access log
      const logRef = push(ref(database, 'accessLogs'));
      await set(logRef, {
        userId:       visit.guestId,
        userName:     visit.guestName,
        method:       'rfid_checkout',
        status:       'completed',
        reason:       `Guest visit completed — ${visit.activityTitle}`,
        timestamp:    checkOutTime,
        rfidCard:     visit.cardUid,
        completedBy:  currentUser?.uid || '',
        duration:     actualDuration,
      });

      toast('success', 'Visit Completed', `${visit.guestName}'s visit has been recorded`);
      onSuccess();
    } catch (err) {
      console.error(err);
      toast('error', 'Error', 'Failed to complete visit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>

      <motion.div
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="p-6 text-white" style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">Complete Visit</h2>
            <button onClick={onClose} className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
              <XMarkIcon className="h-5 w-5 text-white" />
            </button>
          </div>
          <p className="text-white/60 text-sm mt-1">Confirm checkout and release RFID card</p>
        </div>

        <div className="p-6 space-y-4">
          {/* Guest info */}
          <div className="flex items-center gap-4 p-4 rounded-2xl" style={{ background: '#f9fafb' }}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-lg flex-shrink-0"
              style={{ background: PRIMARY }}>
              {(visit.guestName || 'G')[0].toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="font-bold text-gray-900">{visit.guestName}</p>
              <p className="text-sm text-gray-500">{visit.guestPhone || 'No phone'}</p>
              {visit.isRegisteredMember && (
                <span className="text-xs font-medium text-blue-600">Registered Member</span>
              )}
            </div>
          </div>

          {/* Visit details */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Activity',   value: visit.activityTitle },
              { label: 'RFID Card',  value: `${visit.cardName} (${visit.cardUid})` },
              { label: 'Checked In', value: formatTime(visit.checkedIn) },
              { label: 'Duration',   value: formatDuration(duration) },
              { label: 'Amount Paid', value: `KES ${visit.amountPaid?.toLocaleString()}` },
              { label: 'Payment',    value: visit.paymentMethod },
            ].map((item, i) => (
              <div key={i} className="p-3 rounded-xl" style={{ background: '#f9fafb' }}>
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className="font-semibold text-gray-900 text-sm mt-0.5">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Schedule labels */}
          {visit.scheduleLabels?.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Schedules attended</p>
              <div className="flex flex-wrap gap-2">
                {visit.scheduleLabels.map((s, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 rounded-lg font-medium"
                    style={{ background: `${PRIMARY}10`, color: PRIMARY }}>{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Confirm note */}
          <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              Confirming this will mark the visit as complete and make the RFID card available again.
              Ensure the guest has returned the card.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button onClick={onClose}
              className="flex-1 py-3 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
              Cancel
            </button>
            <button onClick={handleComplete} disabled={loading}
              className="flex-1 py-3 rounded-xl text-sm font-bold text-white shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              style={{ background: `linear-gradient(135deg,${PRIMARY},#b30000)` }}>
              {loading
                ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <><CheckCircleIcon className="h-4 w-4" /> Confirm Checkout</>
              }
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// RFID CARD MODAL (FULL SCREEN)
// ═══════════════════════════════════════════════════════════════════════════════
const RfidCardModal = ({ cards, currentUser, userData, onClose, onSuccess }) => {
  const [cardName,  setCardName]  = useState('');
  const [cardUid,   setCardUid]   = useState('');
  const [loading,   setLoading]   = useState(false);
  const [deleting,  setDeleting]  = useState(null);

  const handleAddCard = async (e) => {
    e.preventDefault();
    if (!cardName.trim()) return toast('error', 'Card name required', 'Give this card a friendly name');
    if (!cardUid.trim())  return toast('error', 'Card UID required',  'Enter the unique ID printed on the card');

    // Check duplicate UID
    const exists = cards.find(c => c.uid.toLowerCase() === cardUid.trim().toLowerCase());
    if (exists) return toast('error', 'Duplicate UID', `A card with UID "${cardUid.trim()}" already exists`);

    setLoading(true);
    try {
      const now    = Date.now();
      const newRef = push(ref(database, 'rfidCards'));
      await set(newRef, {
        id:             newRef.key,
        name:           cardName.trim(),
        uid:            cardUid.trim().toUpperCase(),
        status:         'available',
        assignedTo:     '',
        assignedToName: '',
        visitId:        '',
        validUntil:     0,
        activity:       '',
        activatedBy:    '',
        activatedAt:    0,
        addedBy:        currentUser?.uid || '',
        addedByName:    userData?.personalInfo?.name || 'Admin',
        createdAt:      now,
      });
      toast('success', 'Card Added', `${cardName.trim()} added to the pool`);
      setCardName('');
      setCardUid('');
      onSuccess();
    } catch (err) {
      console.error(err);
      toast('error', 'Error', 'Failed to add card');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCard = async (card) => {
    if (card.status === 'active') {
      return toast('error', 'Card In Use', 'Cannot delete a card that is currently assigned to a guest');
    }
    const ok = await Swal.fire({
      title: `Delete "${card.name}"?`,
      text: 'This card will be removed from the pool permanently.',
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: PRIMARY, cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete',
      customClass: { container: 'swal-topz' },
    });
    if (!ok.isConfirmed) return;
    setDeleting(card.id);
    try {
      await set(ref(database, `rfidCards/${card.id}`), null);
      toast('success', 'Deleted', `${card.name} removed from pool`);
      onSuccess();
    } catch (err) {
      toast('error', 'Error', 'Failed to delete card');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 flex"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>

      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 320 }}
        className="ml-auto flex flex-col bg-white shadow-2xl"
        style={{ width: '100%', maxWidth: '600px', height: '100vh' }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-8 py-4"
          style={{ background: `linear-gradient(135deg,${DARK} 0%,${PRIMARY} 100%)` }}>
          <div>
            <h2 className="text-xl font-bold text-white">RFID Card Pool</h2>
            <p className="text-white/60 text-sm mt-0.5">Add and manage physical RFID cards</p>
          </div>
          <button onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl text-white"
            style={{ background: 'rgba(255,255,255,0.15)' }}>
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-8" style={{ minHeight: 0 }}>

          {/* Add Card Form */}
          <ModalSection title="Add New Card">
            <p className="text-xs text-gray-500 mb-4">
              Enter the UID printed on the physical RFID card (usually an 8–10 character code on the back of the card).
            </p>
            <form onSubmit={handleAddCard} className="space-y-4">
              <div>
                <ModalLabel>Card Name *</ModalLabel>
                <div className="relative">
                  <IdentificationIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input value={cardName} onChange={e => setCardName(e.target.value)}
                    placeholder="e.g., Card 01, Red Card, VIP Card"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': PRIMARY }} />
                </div>
                <p className="text-xs text-gray-400 mt-1">A friendly name to identify this card at reception</p>
              </div>
              <div>
                <ModalLabel>Card UID *</ModalLabel>
                <div className="relative">
                  <QrCodeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input value={cardUid} onChange={e => setCardUid(e.target.value)}
                    placeholder="e.g., A3F2B1C4"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 transition-all"
                    style={{ '--tw-ring-color': PRIMARY }} />
                </div>
                <p className="text-xs text-gray-400 mt-1">The unique ID printed on the card. Usually on the back.</p>
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                style={{ background: `linear-gradient(135deg,${PRIMARY},#b30000)` }}>
                {loading
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <><PlusIcon className="h-4 w-4" /> Add Card to Pool</>
                }
              </button>
            </form>
          </ModalSection>

          {/* Existing Cards */}
          <ModalSection title={`Card Pool (${cards.length})`}>
            {cards.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No cards added yet.</p>
            ) : (
              <div className="space-y-3">
                {cards.map(card => (
                  <div key={card.id}
                    className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:shadow-sm transition-all bg-white">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: card.status === 'available' ? '#f0fdf4' : `${PRIMARY}10` }}>
                      <QrCodeIcon className="h-5 w-5"
                        style={{ color: card.status === 'available' ? '#16a34a' : PRIMARY }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 text-sm">{card.name}</p>
                      <p className="font-mono text-xs text-gray-400">{card.uid}</p>
                      {card.status === 'active' && card.assignedToName && (
                        <p className="text-xs mt-0.5" style={{ color: PRIMARY }}>
                          In use by {card.assignedToName}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${card.status === 'available' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {card.status === 'available' ? 'Available' : 'In Use'}
                      </span>
                      <button
                        onClick={() => handleDeleteCard(card)}
                        disabled={deleting === card.id || card.status === 'active'}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed">
                        {deleting === card.id
                          ? <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                          : <TrashIcon className="h-4 w-4" />
                        }
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ModalSection>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-end px-8 py-4 border-t border-gray-100 bg-white">
          <button onClick={onClose}
            className="px-8 py-2.5 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">
            Close
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── SHARED MODAL COMPONENTS ──────────────────────────────────────────────────
const ModalLabel = ({ children }) => (
  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">{children}</label>
);

const ModalSection = ({ title, children }) => (
  <div>
    <div className="flex items-center gap-3 mb-5">
      <p className="text-base font-bold text-gray-900 whitespace-nowrap">{title}</p>
      <div className="flex-1 h-px bg-gray-100" />
    </div>
    {children}
  </div>
);

export default GuestManagement;